// React to changes in multiChildValue (from MultiChild dropdown)

import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { axios } from 'services/axios';
import { useQuery } from 'hooks/useCustomQuery';
import FormBase from '@/components/forms/new/FormBase';
import WholesalerField from '@/components/pickers/WholesalerField';
import CarrierField from '@/components/pickers/CarrierField';
import PartnerCompanyField from '@/components/pickers/PartnerCompanyField';
import PublicPoolField from '../add/formFields/PublicPoolField';
import GlobalEntityField from '../add/formFields/GlobalEntityField';
import CountryField from './formFields/CountryField';
import NotesField from './formFields/NotesField';
import OutboundCallerIDField from './formFields/OutboundCallerIDField';
import { Alert, AlertDescription, AlertIcon } from 'components/v4';

// Read the multiChildValue prop (from MultiChild dropdown)
// You can use this value for any logic that needs to react to the dropdown
// Example: log it for now
// (This must come after imports)
// (If you want to use this, place it inside the component, not at the top level)

const CompanyIDSync = ({ companyID, onSync }) => {
    useEffect(() => {
        if (companyID) onSync(companyID);
    }, [companyID, onSync]);
    return null;
};

const DEFAULT_DATA = {
    wholesalerID: null,
    inboundCarrierID: null,
    outboundCarrierID: null,
    partnerID: null,
    companyID: null,
    partner: '',
    company: '',
    publicPool: false,
    globalEntity: false,
    country: '',
    internalNotes: '',
    cliPresentation: null,
};

const NumberBlocksGeneralForm = ({
    child,
    onSuccess,
    onBack,
    formDraft = {},
    setFormDraft = () => {},
    multiChildValue,
}) => {
    const { requiredScope, currentScope } = useSelector((state) => state.login);
    const { darkMode } = useSelector((state) => state.settings);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [carrierCount, setCarrierCount] = useState(0);
    const [currentCompanyID, setCurrentCompanyID] = useState(null);
    const formikCompanyIDRef = useRef(null);
    const prevValuesRef = useRef(null);
    // Callback to sync Formik's companyID to local state
    const handleFormikCompanyIDChange = (companyID) => {
        if (companyID && companyID !== formikCompanyIDRef.current) {
            formikCompanyIDRef.current = companyID;
            setCurrentCompanyID(companyID);
        }
    };
    // Fetch wholesaler options once on mount (only for scope 80 - global admins)
    const { data: wholesalerOptions = [] } = useQuery({
        queryKey: ['wholesalers'],
        queryFn: async () => {
            const res = await axios.get('/wholesalers');
            return res.data || [];
        },
        enabled: currentScope >= 80,
    });

    const effectiveChild =
        multiChildValue && multiChildValue.id !== child?.id
            ? multiChildValue
            : child;

    const { data: outboundCallerIDOptions = [] } = useQuery({
        queryKey: ['companyNumbers', currentCompanyID],
        queryFn: async () => {
            const res = await axios.get(
                `/company/${currentCompanyID}/numbers/presentable`,
            );
            return (res.data || []).map(({ telephoneNumber }) => ({
                telephoneNumber,
                display: '+' + telephoneNumber,
            }));
        },
        enabled: Boolean(currentCompanyID),
    });

    const {
        data: backendData = DEFAULT_DATA,
        isLoading: loading,
        isError: notFound,
    } = useQuery({
        queryKey: ['numberBlockGeneral', effectiveChild?.id],
        queryFn: async () => {
            const res = await axios.get(`/numberblock/${effectiveChild.id}`);
            return { ...DEFAULT_DATA, ...res.data };
        },
        enabled: Boolean(effectiveChild?.id),
        retry: false,
    });

    // Reset formDraft when the selected record changes
    useEffect(() => {
        if (effectiveChild?.id) setFormDraft({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveChild?.id]);

    // Always use the latest draft for initialValues unless id changes
    const mergedInitialValues = { ...backendData, ...formDraft };

    // Submit handler
    const handleSubmit = (values, actions) => {
        setIsLoading(true);
        axios
            .put('/numberblock/', { ...values, id: child?.id })
            .then(() => {
                setFormDraft({});
                if (onSuccess) onSuccess();
            })
            .catch((err) => {
                setError(err);
            })
            .finally(() => {
                setIsLoading(false);
                actions.setSubmitting(false);
            });
    };

    // Track form changes
    const handleValuesChange = (values) => {
        setFormDraft(values);
        setError(null);
    };

    if (loading) return null;

    if (notFound) {
        return (
            <Alert status="error" variant="left-accent">
                <AlertIcon />
                <AlertDescription>
                    This number block no longer exists. It may have already been
                    deleted.
                </AlertDescription>
            </Alert>
        );
    }

    // Use a stable key for FormBase so it does not remount on tab switch for the same record
    // Only reinitialize when id or draft changes
    return (
        <>
            <FormBase
                key="number-block-form"
                initialValues={mergedInitialValues}
                finalFormSubmit={handleSubmit}
                enableReinitialize={true}
                backLink={['/number-blocks', { state: { index: 0 } }]}
                isSubmitDisabled={
                    isLoading ||
                    (carrierCount >= 1 &&
                        (!formDraft.inboundCarrierID ||
                            !formDraft.outboundCarrierID))
                }
                onBack={onBack}
                customClick={true}>
                {(formikProps) => {
                    const { values, setFieldValue, errors, touched } =
                        formikProps;

                    // Track form changes (without useEffect)
                    if (prevValuesRef.current !== values) {
                        prevValuesRef.current = values;
                        handleValuesChange(values);
                    }

                    // ...existing code...
                    return (
                        <div style={{ paddingBottom: '10px' }}>
                            <CompanyIDSync
                                companyID={values.companyID}
                                onSync={handleFormikCompanyIDChange}
                            />
                            {/* Wholesaler: only shown for scope 80 (global admins) */}
                            {requiredScope >= 80 && (
                                <WholesalerField
                                    darkMode={darkMode}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    errors={errors}
                                    touched={touched}
                                    requiredScope={requiredScope}
                                    onError={setError}
                                    wholesalerOptions={wholesalerOptions}
                                />
                            )}
                            {/* Partner: not shown for scope 40 (partners) */}
                            {requiredScope > 40 && (
                                <PartnerCompanyField
                                    darkMode={darkMode}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    errors={errors}
                                    touched={touched}
                                    mode="partner"
                                    label="Partner"
                                    description="Select the partner for this number block."
                                />
                            )}
                            {/* Company: always shown */}
                            <PartnerCompanyField
                                darkMode={darkMode}
                                values={values}
                                setFieldValue={setFieldValue}
                                errors={errors}
                                touched={touched}
                                mode="company"
                                label="Company"
                                description="Select the company for this number block."
                            />
                            {/* Carrier: combined field, not shown for scope 40 */}
                            <CarrierField
                                darkMode={darkMode}
                                values={values}
                                setFieldValue={setFieldValue}
                                errors={errors}
                                touched={touched}
                                requiredScope={requiredScope}
                                onError={setError}
                                onCarriersLoaded={setCarrierCount}
                            />
                            {/* Public Pool */}
                            <PublicPoolField
                                darkMode={darkMode}
                                values={values}
                                setFieldValue={setFieldValue}
                            />
                            {requiredScope >= 80 && (
                                <GlobalEntityField
                                    darkMode={darkMode}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    isReadOnly
                                />
                            )}
                            {/* Country */}
                            <CountryField darkMode={darkMode} values={values} />
                            {/* Notes: only shown for scope > 60 */}
                            <NotesField
                                darkMode={darkMode}
                                values={values}
                                setFieldValue={setFieldValue}
                                errors={errors}
                                touched={touched}
                                requiredScope={requiredScope}
                            />
                            {/* Outbound Caller ID */}
                            <OutboundCallerIDField
                                darkMode={darkMode}
                                values={values}
                                setFieldValue={setFieldValue}
                                onError={setError}
                                outboundCallerIDOptions={
                                    outboundCallerIDOptions
                                }
                            />
                            {error && (
                                <Alert
                                    status="error"
                                    sx={{ marginTop: '1rem' }}>
                                    <AlertIcon />
                                    <AlertDescription>
                                        {error?.response?.data?.message ||
                                            'An error occurred. Please try again later.'}
                                    </AlertDescription>
                                </Alert>
                            )}
                        </div>
                    );
                }}
            </FormBase>
        </>
    );
};

export default NumberBlocksGeneralForm;
